import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { pluginAppInIframe } from '../../util/appUtils';
import { useShellAppContext } from '../../features/shell/context/useShellAppContext';
import { buildMenuData } from './menu/menuData';
import { buildSidebar } from './menu/sidebarBuilder';
import { resolveLinks } from './menu/sidebarUtils';
import {
  loadConfigFailure,
  loadConfigRequest,
  loadConfigSuccess,
} from '../../features/search-experience-v2/api/searchExperienceSlice';

export const MenuDataContext = React.createContext();

// Regex to extract :shopId from /app/:shopId/* canonical paths.
const APP_SHOP_PATH = /^\/app\/([^/]+)\//;

/**
 * Build a minimal menu-data object for the embedded production plugin-admin shell.
 * Bypasses the settings.isFetched guard — the shell is bridge-backed and never
 * fetches gateway settings.
 * Disabled states are derived from shell session capabilities, not from legacy
 * cloud-transport flags.
 */
const buildEmbeddedMenuData = (shopId, capabilities = {}, searchExperience = {}) => {
  const searchViews = searchExperience?.config?.search?.views || {};
  const sidebar = buildSidebar({
    shopId,
    onboarding: { search: { completed: true }, recs: { completed: true } },
    searchEnabled: false,
    recsEnabled: false,
    anySearchEnabled: false,
    anyRecsEnabled: false,
    isLoaded: true,
    experimentIsDisabled: () => true,
    platformSettings: { isFetched: true },
    availableViews: Object.keys(searchViews),
    searchMode: searchExperience?.config?.search?.searchExperience || null,
    capabilities,
    productId: 'plugin',
  });
  return { ...resolveLinks(sidebar, shopId) };
};

export const MenuDataProvider = ({ shopId, children }) => {
  const dispatch = useDispatch();
  const [menuData, setMenuData] = useState(null);
  const { settings, onboarding, searchExperience } = useSelector((store) => store);
  const location = useLocation();
  // capabilities and routeShopId come from the single source of truth: ShellAppContext.
  // Outside the provider tree (non-iframe mode) these fall back to the context defaults ({} / null).
  const {
    capabilities,
    routeShopId: shellShopId,
    gatewayClient,
    productProfile,
  } = useShellAppContext();
  const urlMatch = location.pathname.match(APP_SHOP_PATH);
  const urlShopId = urlMatch ? urlMatch[1] : null;
  const embeddedShopId = shellShopId ?? urlShopId;
  const embeddedMode = pluginAppInIframe();

  useEffect(() => {
    if (embeddedMode && embeddedShopId) {
      setMenuData(buildEmbeddedMenuData(embeddedShopId, capabilities, searchExperience));
      return;
    }
    const data = buildMenuData(
      shopId,
      settings,
      onboarding,
      searchExperience,
      capabilities,
      productProfile?.id || null,
    );
    setMenuData(data);
  }, [shopId, settings, onboarding, searchExperience, capabilities, productProfile, embeddedMode, embeddedShopId]);

  useEffect(() => {
    if (!embeddedMode || !embeddedShopId || !gatewayClient) return undefined;
    if (searchExperience?.config || searchExperience?.isLoading) return undefined;

    dispatch(loadConfigRequest());

    gatewayClient.loadSearchExperienceConfig(embeddedShopId, 'preview')
      .then((config) => {
        dispatch(loadConfigSuccess({ config, mode: 'preview' }));
      })
      .catch((error) => {
        dispatch(loadConfigFailure({
          error: error?.message || 'Failed to load search experience config',
        }));
      });

    return undefined;
  }, [
    dispatch,
    embeddedMode,
    embeddedShopId,
    gatewayClient,
    searchExperience?.config,
    searchExperience?.isLoading,
  ]);

  return (
    <MenuDataContext.Provider value={menuData}>
      {children}
    </MenuDataContext.Provider>
  );
};
